import BaseStore from 'customer-flows/stores/base-store'
import { context } from 'customer-flows/utils/core'
import { TrackedMap } from 'tracked-built-ins'
import { matchTicketsByName } from 'customer-flows/utils/bundle-tickets'
import { createBundleAvailability, hasSelectableTime } from 'customer-flows/bundle-availability/engine'

// Off-instance so the cache never lands in the store's own-property iteration (toJSON).
const availabilities = new WeakMap<BundleStoreClass, BundleAvailability>()

export default class BundleStoreClass extends BaseStore {
  @context('activity') tickets: ActivityStore['tickets']
  @context('tickets') getCountForTicket: TicketsStore['getCountForTicket']
  @context('quote:id') quoteId: QuoteStore['id']
  @context('program/booking-flow:bundleItems') bundleItems: BookingFlowStore['bundleItems']

  // What the customer set by hand: item id -> ticket id -> quantity
  quantitiesByBundleItem = new TrackedMap<string, Record<string, number>>()

  get mainTickets(): Array<{ name: string; quantity: number }> {
    return (this.tickets ?? []).map((ticket) => ({ name: ticket.name, quantity: this.getCountForTicket(ticket.id) }))
  }

  // `EXACT` follows the main booking and the customer cannot change it
  isAutomatic(item: BundleItem): boolean {
    return item.ticketQuantityMatchMode === 'EXACT'
  }

  // `PRESELECT` also starts from the main booking, until the customer changes it
  isPrefilled(item: BundleItem): boolean {
    return this.isAutomatic(item) || item.ticketQuantityMatchMode === 'PRESELECT'
  }

  hasSeveralTickets(item: BundleItem): boolean {
    return (item.tickets?.length ?? 0) > 1
  }

  getTicketQuantities(item: BundleItem): Record<string, number> {
    const picked = this.quantitiesByBundleItem.get(item.id)

    if (this.isAutomatic(item) || (this.isPrefilled(item) && !picked)) {
      return matchTicketsByName(this.mainTickets, item.tickets ?? [])
    }

    return picked ?? {}
  }

  getTicketQuantity(item: BundleItem, ticketId: string): number {
    return this.getTicketQuantities(item)[ticketId] ?? 0
  }

  getQuantity(item: BundleItem): number {
    return Object.values(this.getTicketQuantities(item)).reduce((total, quantity) => total + quantity, 0)
  }

  // The items of one flow belong to one bundle
  // TODO(DEV-13999): remove this derivation, the widget will have the bundle id upfront
  get bundleId(): string {
    const bundleId = this.bundleItems.find((item) => item.bundle?.id)?.bundle?.id

    if (!bundleId) throw new Error('The flow has no bundle')

    return bundleId
  }

  // Lazily built so the store's owner is set before the cache (a destroyable child of this store) is created.
  get availability(): BundleAvailability {
    let availability = availabilities.get(this)
    if (!availability) {
      availability = createBundleAvailability(this, () => ({ quoteId: this.quoteId, bundleId: this.bundleId }))
      availabilities.set(this, availability)
    }
    return availability
  }

  get isAvailabilityLoading(): boolean {
    return this.availability.state === 'loading'
  }

  get hasAvailabilityError(): boolean {
    return this.availability.state === 'error'
  }

  // Without an answer for the item, it cannot be added
  isItemAvailable(itemId: string): boolean {
    return hasSelectableTime(this.availability.getItem(itemId))
  }
}

declare global {
  export type BundleStore = BundleStoreClass
  export interface StoreRegistry {
    bundle: BundleStore
  }
}
